/**
 * The For Review inbox: the `/api/dashboard/practice` payload flattened into
 * one list of things that want the attorney, grouped by urgency (Overdue /
 * Waiting on You / Coming Up) with the kind kept visible as the row icon.
 * Pure functions plus two small localStorage hooks for read and dismissed
 * state; no API calls.
 */
import dayjs from "dayjs";

import { workflowDisplayText } from "@/features/caseIde/workflows/catalog";
import { isCalendarDate, parseDateOnly } from "@/lib/dateOnly";
import {
  AtSign,
  CalendarCheck,
  CalendarClock,
  FileClock,
  FileWarning,
  Send,
  Stamp,
  UserPlus,
  type LucideIcon
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { itemRoute } from "@/features/agentAttention/items";
import {
  SIGNAL_META,
  signalAsk,
  signalFace,
  type SignalFace
} from "@/features/agentAttention/signalTone";
import type { AttentionItem } from "@/features/agentAttention/types";

import type {
  AttentionThreadRow,
  ExpirationRow,
  FilingDeadlineRow,
  IntakeRow,
  PracticeDashboard,
  ReminderRow
} from "../types";

// Kinds and buckets ------------------------------------------------------------

export type TriageKind =
  | "overdue"
  | "approval"
  | "mention"
  | "today"
  | "intake"
  | "filing"
  | "expiring"
  | "with_client";

export type TriageBucket = "overdue" | "waiting" | "later";

/** Each kind's icon and label; the array order also ranks kinds inside a bucket. */
export const KIND_META: ReadonlyArray<{
  kind: TriageKind;
  label: string;
  icon: LucideIcon;
}> = [
  { kind: "overdue", label: "Overdue", icon: CalendarClock },
  { kind: "approval", label: "Agents", icon: Stamp },
  { kind: "mention", label: "Mentions", icon: AtSign },
  { kind: "today", label: "Due Today", icon: CalendarCheck },
  { kind: "intake", label: "Intakes to Review", icon: UserPlus },
  { kind: "filing", label: "Filing Deadlines", icon: FileClock },
  { kind: "expiring", label: "Expiring Documents", icon: FileWarning },
  { kind: "with_client", label: "Intakes With the Client", icon: Send }
];

export const KIND_ICON: Record<TriageKind, LucideIcon> = Object.fromEntries(
  KIND_META.map((k) => [k.kind, k.icon])
) as Record<TriageKind, LucideIcon>;

export const KIND_LABEL: Record<TriageKind, string> = Object.fromEntries(
  KIND_META.map((k) => [k.kind, k.label])
) as Record<TriageKind, string>;

export const BUCKETS: ReadonlyArray<{ id: TriageBucket; label: string }> = [
  { id: "overdue", label: "Overdue" },
  { id: "waiting", label: "Waiting on You" },
  { id: "later", label: "Coming Up" }
];

/** Filing deadlines within this many days make the inbox. */
export const FILING_WINDOW_DAYS = 7;
/** Expirations within this many days make the inbox (expired ones always do). */
export const EXPIRATION_WINDOW_DAYS = 30;

// Items ------------------------------------------------------------------------

export type TriageItem = {
  /** Stable across refreshes: `{kind}:{record id}`. */
  id: string;
  kind: TriageKind;
  bucket: TriageBucket;
  title: string;
  clientId: string | null;
  clientName: string;
  /** Where the primary "open" action goes. */
  path: string;
  /** The row's markdown ref, for hover cards. */
  ref: string;
  /** Due instant (reminders, deadlines, expirations); null for threads/intakes. */
  due: string | null;
  /** When the underlying record last changed; drives unread + dismissal keys. */
  changedAt: string | null;
  overdue: boolean;
  /** Days until due (negative when past). */
  daysLeft: number | null;
  assignedToMe: boolean;
  /** Assignee names, when the record carries them. */
  assignees: string[];
  /** A short description for the detail pane. */
  detail: string;
  /** Secondary label ("H-1B · Northwind Labs" / "Awaiting review"). */
  caseLabel: string;
  /** An agent outcome's kind (approval, question, failed, problems, finished). */
  signal?: SignalFace;
  threadId?: string;
  threadScopeKind?: string;
  threadScopeId?: string;
  reminderId?: string;
  intakeId?: string;
  individualId?: string;
  /** The raw row, for kind-specific rendering. */
  row:
    | ReminderRow
    | AttentionThreadRow
    | IntakeRow
    | FilingDeadlineRow
    | ExpirationRow;
};

const text = (value: unknown, fallback = ""): string => {
  const s = typeof value === "string" ? value.trim() : "";
  return s || fallback;
};

const kindRank = (kind: TriageKind): number =>
  KIND_META.findIndex((k) => k.kind === kind);

/**
 * A lead reminder reads as the document it leads: "EAD expires in 44d", not
 * "EAD expiration approaching, 46d late". Null for any other reminder.
 */
export const reminderEventTitle = (r: ReminderRow): string | null => {
  if (!r.event_date || !r.event_label) return null;
  const d = r.days_left;
  if (r.event_kind === "child_turns_18")
    return d < 0
      ? `Dependent turned 18 ${-d}d ago`
      : d === 0
      ? "Dependent turns 18 today"
      : `Dependent turns 18 in ${d}d`;
  return d < 0
    ? `${r.event_label} expired ${-d}d ago`
    : d === 0
    ? `${r.event_label} expires today`
    : `${r.event_label} expires in ${d}d`;
};

/** "Reminder 46d overdue": the reminder's own lateness, as a secondary fact. */
export const reminderSecondary = (r: ReminderRow): string => {
  if (r.kind === "appointment") return "Appointment";
  const late = r.reminder_days_late ?? 0;
  return r.event_date && r.reminder_overdue && late > 0
    ? `Reminder ${late}d overdue`
    : "Reminder";
};

const reminderItem = (r: ReminderRow, uid: string): TriageItem => ({
  id: `${r.overdue ? "overdue" : "today"}:${r.reminder_id}`,
  kind: r.overdue ? "overdue" : "today",
  bucket: r.overdue ? "overdue" : "waiting",
  title: reminderEventTitle(r) ?? text(r.title, "(Untitled)"),
  clientId: r.foreign_national_id || null,
  clientName: text(r.client, "Client"),
  path: r.path,
  ref: r.ref,
  due: r.event_date ?? r.due,
  changedAt: r.due,
  overdue: Boolean(r.overdue),
  daysLeft: typeof r.days_left === "number" ? r.days_left : null,
  assignedToMe: (r.assigned_to_user_ids ?? []).includes(uid),
  assignees: r.assignees ?? [],
  detail: text(r.description),
  caseLabel: reminderSecondary(r),
  reminderId: r.reminder_id,
  row: r
});

const threadItem = (t: AttentionThreadRow): TriageItem => {
  const kind: TriageKind = t.reason === "approval" ? "approval" : "mention";
  return {
    id: `${kind}:${t.thread_id}`,
    kind,
    bucket: "waiting",
    title: text(t.title, "Untitled thread"),
    clientId: t.scope_kind === "client" ? t.scope_id : null,
    clientName:
      t.scope_kind === "client" ? text(t.detail, "Client") : "Practice",
    path: t.path,
    ref: t.ref,
    due: null,
    changedAt: t.updated_at,
    overdue: false,
    daysLeft: null,
    // Approvals and mentions are, by construction, for the signed-in user.
    assignedToMe: true,
    assignees: [],
    detail: workflowDisplayText(text(t.last_message_preview)) || text(t.detail),
    caseLabel:
      t.reason === "approval" ? "Needs your approval" : "You were mentioned",
    threadId: t.thread_id,
    threadScopeKind: t.scope_kind,
    threadScopeId: t.scope_id,
    row: t
  };
};

const intakeItem = (i: IntakeRow): TriageItem => ({
  id: `intake:${i.outgoing_intake_id}`,
  kind: "intake",
  bucket: "waiting",
  title: text(i.title, "Intake"),
  clientId: i.foreign_national_id || null,
  clientName: text(i.client, "Client"),
  path: i.path,
  ref: i.ref,
  due: null,
  changedAt: i.updated_at ?? i.sent_at,
  overdue: false,
  daysLeft: null,
  assignedToMe: false,
  assignees: [],
  detail: i.recipient_email ? `Submitted by ${i.recipient_email}` : "",
  caseLabel: "Awaiting review",
  intakeId: i.outgoing_intake_id,
  row: i
});

const WITH_CLIENT_STATUS: Record<string, string> = {
  sent: "Sent, not started",
  in_progress: "Started, not submitted",
  revision_requested: "Revisions requested"
};

/** Whole days since the intake went out (or last changed, for revisions). */
export const daysWaiting = (i: IntakeRow, now: Date = new Date()): number => {
  const since = dayjs(i.sent_at ?? i.updated_at ?? undefined);
  return since.isValid()
    ? Math.max(0, dayjs(now).startOf("day").diff(since.startOf("day"), "day"))
    : 0;
};

/** An intake the client hasn't sent back yet: sent, started or out for revisions. */
const withClientItem = (i: IntakeRow): TriageItem => ({
  id: `with_client:${i.outgoing_intake_id}`,
  kind: "with_client",
  bucket: "later",
  title: text(i.title, "Intake"),
  clientId: i.foreign_national_id || null,
  clientName: text(i.client, "Client"),
  path: i.path,
  ref: i.ref,
  due: null,
  changedAt: i.updated_at ?? i.sent_at,
  overdue: false,
  daysLeft: null,
  assignedToMe: false,
  assignees: [],
  detail: i.recipient_email ? `Sent to ${i.recipient_email}` : "",
  caseLabel: WITH_CLIENT_STATUS[i.status] ?? "With the client",
  intakeId: i.outgoing_intake_id,
  row: i
});

/** False for rows that are only there to keep the attorney informed. */
export const waitsOnAttorney = (item: Pick<TriageItem, "kind">): boolean =>
  item.kind !== "with_client";

const filingItem = (d: FilingDeadlineRow, uid: string): TriageItem => ({
  id: `filing:${d.individual_id}`,
  kind: "filing",
  bucket: d.overdue ? "overdue" : "later",
  title: `${text(d.visa_type, "Filing")} filing deadline`,
  clientId: d.foreign_national_id || null,
  clientName: text(d.applicant, "Applicant"),
  path: d.path,
  ref: d.ref,
  due: d.due,
  changedAt: d.due,
  overdue: Boolean(d.overdue),
  daysLeft: typeof d.days_left === "number" ? d.days_left : null,
  assignedToMe: (d.assigned_to_user_ids ?? []).includes(uid),
  assignees: d.assignees ?? [],
  detail: d.status ? `Case status: ${d.status}` : "",
  caseLabel: [d.visa_type, d.status].filter(Boolean).join(" · "),
  individualId: d.individual_id,
  row: d
});

const expirationItem = (e: ExpirationRow): TriageItem => ({
  id: `expiring:${e.kind}:${e.dependent_id ?? e.foreign_national_id}`,
  kind: "expiring",
  bucket: e.overdue ? "overdue" : "later",
  title: `${text(e.label, "Document")} ${e.overdue ? "expired" : "expiring"}`,
  clientId: e.foreign_national_id || null,
  clientName: text(e.client, "Client"),
  path: e.path,
  ref: e.ref,
  due: e.due,
  changedAt: e.due,
  overdue: Boolean(e.overdue),
  daysLeft: typeof e.days_left === "number" ? e.days_left : null,
  assignedToMe: false,
  assignees: [],
  detail: e.dependent ? `${e.dependent}'s ${text(e.label).toLowerCase()}` : "",
  caseLabel: e.dependent ? `Dependent · ${e.dependent}` : text(e.subject),
  row: e
});

const newestFirst = (a: TriageItem, b: TriageItem): number =>
  Date.parse(b.changedAt ?? "") - Date.parse(a.changedAt ?? "") || 0;

const soonestFirst = (a: TriageItem, b: TriageItem): number =>
  (a.daysLeft ?? 0) - (b.daysLeft ?? 0) ||
  Date.parse(a.due ?? "") - Date.parse(b.due ?? "") ||
  0;

export const orderForRail = (items: TriageItem[]): TriageItem[] =>
  BUCKETS.flatMap((b) =>
    items
      .filter((t) => t.bucket === b.id)
      .map((t, i) => ({ t, i }))
      .sort((x, y) => {
        if (b.id === "later") {
          // Dated items first; intakes with the client wait at the end.
          return (
            Number(!x.t.due) - Number(!y.t.due) ||
            soonestFirst(x.t, y.t) ||
            x.i - y.i
          );
        }
        if (b.id === "overdue") {
          // Most late first.
          return (
            (x.t.daysLeft ?? 0) - (y.t.daysLeft ?? 0) ||
            kindRank(x.t.kind) - kindRank(y.t.kind) ||
            x.i - y.i
          );
        }
        return (
          kindRank(x.t.kind) - kindRank(y.t.kind) ||
          newestFirst(x.t, y.t) ||
          x.i - y.i
        );
      })
      .map((x) => x.t)
  );

/**
 * An agent outcome from the attention list, as a For Review row. The chip
 * names its kind and the context line carries the specific ask ("Approve
 * switching to Plan mode").
 */
export const signalItem = (a: AttentionItem): TriageItem => {
  const face = signalFace(a);
  const row: AttentionThreadRow = {
    thread_id: a.thread_id,
    scope_kind: a.scope_kind,
    scope_id: a.scope_id,
    agent_kind: "assistant",
    title: a.title,
    reason: "approval",
    detail: a.client_name ?? "",
    last_message_preview: signalAsk(a),
    updated_at: a.since,
    path: itemRoute(a),
    ref: ""
  };
  return {
    ...threadItem(row),
    id: `approval:${a.thread_id}:${a.since ?? ""}`,
    signal: face,
    clientId: a.scope_kind === "client" ? a.scope_id : null,
    clientName:
      a.client_name || (a.scope_kind === "practice" ? "Practice" : "Client"),
    caseLabel: text(a.issues) || text(a.summary) || SIGNAL_META[face].line
  };
};

/**
 * Everything in the practice payload that wants the signed-in attorney,
 * ordered for the rail: by bucket; inside Coming Up soonest first, elsewhere
 * by kind then newest. Agent outcomes come from the attention list
 * (`signals`); the payload's thread rows only contribute mentions.
 */
export const buildTriage = (
  summary: PracticeDashboard | null | undefined,
  uid: string,
  signals: AttentionItem[] = []
): TriageItem[] => {
  if (!summary) return [];
  const items: TriageItem[] = [];
  (summary.today?.mine ?? []).forEach((r) => items.push(reminderItem(r, uid)));
  (summary.attention_threads ?? [])
    .filter((t) => t.reason === "mentions")
    .forEach((t) => items.push(threadItem(t)));
  signals.forEach((a) => items.push(signalItem(a)));
  (summary.intakes_awaiting_review ?? []).forEach((i) =>
    items.push(intakeItem(i))
  );
  (summary.intakes_in_flight ?? []).forEach((i) =>
    items.push(withClientItem(i))
  );
  (summary.filing_deadlines ?? [])
    .filter((d) => d.overdue || (d.days_left ?? 99) <= FILING_WINDOW_DAYS)
    .forEach((d) => items.push(filingItem(d, uid)));
  (summary.expirations ?? [])
    .filter((e) => e.overdue || (e.days_left ?? 99) <= EXPIRATION_WINDOW_DAYS)
    .forEach((e) => items.push(expirationItem(e)));
  return orderForRail(items);
};

/**
 * Every dated row in the payload, without the inbox's windows: the runway
 * spans months, so it also places deadlines and expirations further out and
 * the upcoming reminders. Ids match `buildTriage`'s for the same record.
 */
export const buildDated = (
  summary: PracticeDashboard | null | undefined,
  uid: string
): TriageItem[] => {
  if (!summary) return [];
  const seen = new Set<string>();
  const out: TriageItem[] = [];
  const push = (t: TriageItem) => {
    if (seen.has(t.id) || !t.due) return;
    seen.add(t.id);
    out.push(t);
  };
  [...(summary.today?.mine ?? []), ...(summary.upcoming ?? [])].forEach((r) =>
    push(reminderItem(r, uid))
  );
  (summary.filing_deadlines ?? []).forEach((d) => push(filingItem(d, uid)));
  (summary.expirations ?? []).forEach((e) => push(expirationItem(e)));
  return out;
};

export type TriageGroup = {
  id: TriageBucket;
  label: string;
  rows: TriageItem[];
};

/** Non-empty buckets, in rail order. */
export const groupTriage = (items: TriageItem[]): TriageGroup[] =>
  BUCKETS.map((b) => ({
    ...b,
    rows: items.filter((t) => t.bucket === b.id)
  })).filter((b) => b.rows.length > 0);

// Labels -----------------------------------------------------------------------

/**
 * A due stamp as a local day: a calendar date (stored as midnight UTC) is that
 * date wherever the reader is, not the evening before west of Greenwich.
 */
export const dueDay = (value: string | null | undefined): dayjs.Dayjs =>
  isCalendarDate(value)
    ? dayjs(parseDateOnly(value) ?? undefined)
    : dayjs(value ?? undefined);

/**
 * The rail row's right-hand label: "18m" / "3:30 PM" for things today,
 * "12d late" when overdue, "Oct 7" / "in 12d" otherwise.
 */
export const rightLabel = (
  item: TriageItem,
  now: Date = new Date()
): string => {
  const at = dueDay(item.due ?? item.changedAt);
  if (!at.isValid()) return "";
  const allDay =
    isCalendarDate(item.due) || Boolean((item.row as ReminderRow).all_day);
  const n = dayjs(now);
  if (item.overdue && item.due) {
    const days = n.startOf("day").diff(at.startOf("day"), "day");
    if (days <= 0) {
      const hours = n.diff(at, "hour");
      return hours < 1
        ? `${Math.max(1, n.diff(at, "minute"))}m late`
        : `${hours}h late`;
    }
    return `${days}d late`;
  }
  if (item.kind === "with_client") {
    const days = daysWaiting(item.row as IntakeRow, now);
    return days < 1 ? "Sent today" : `${days}d waiting`;
  }
  if (!item.due) {
    // Threads and intakes: how long ago they changed.
    const minutes = n.diff(at, "minute");
    if (minutes < 1) return "now";
    if (minutes < 60) return `${minutes}m`;
    const hours = n.diff(at, "hour");
    if (hours < 24 && at.isSame(n, "day")) return `${hours}h`;
    if (at.isSame(n.subtract(1, "day"), "day")) return "Yesterday";
    return at.format("MMM D");
  }
  if (at.isSame(n, "day")) {
    return allDay ? "Today" : at.format("h:mm A");
  }
  if (item.daysLeft !== null && item.daysLeft <= FILING_WINDOW_DAYS) {
    return `in ${item.daysLeft}d`;
  }
  return at.format("MMM D");
};

/** The detail header's phrasing: "Overdue 12d" / "Due 3:30 PM" / "Due Oct 7". */
export const dueLabel = (item: TriageItem, now: Date = new Date()): string => {
  const label = rightLabel(item, now);
  if (!label) return "";
  if (item.overdue) return `Overdue ${label.replace(" late", "")}`;
  if (item.kind === "with_client") return label;
  if (!item.due) return label === "now" ? "Just now" : `Updated ${label}`;
  return `Due ${label}`;
};

// Filters ----------------------------------------------------------------------

export type TriageFilter = "all" | "unread" | "mine";

export const TRIAGE_FILTERS: ReadonlyArray<{
  id: TriageFilter;
  label: string;
}> = [
  { id: "all", label: "Everything" },
  { id: "unread", label: "Unread" },
  { id: "mine", label: "Assigned to me" }
];

export const applyTriageFilter = (
  items: TriageItem[],
  filter: TriageFilter,
  isUnread: (item: TriageItem) => boolean
): TriageItem[] =>
  items.filter((t) =>
    filter === "unread"
      ? isUnread(t)
      : filter === "mine"
      ? t.assignedToMe
      : true
  );

// Local state --------------------------------------------------------------------

export const READ_STORAGE_KEY = (uid: string) => `home.triage.read.${uid}`;
export const DISMISSED_STORAGE_KEY = (uid: string) =>
  `home.triage.dismissed.${uid}`;
export const READ_CAP = 500;

const readJson = <T>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

/** The key an item is read/dismissed under: it changes when the item does. */
export const stateKey = (item: TriageItem): string =>
  `${item.id}@${item.changedAt ?? ""}`;

type ReadState = {
  isRead: (item: TriageItem) => boolean;
  markRead: (items: TriageItem | TriageItem[]) => void;
  /** The briefing row keeps its own unread flag. */
  briefingRead: boolean;
  markBriefingRead: () => void;
};

const BRIEFING_KEY = "__briefing__";

/**
 * Which items the attorney has opened, capped at the most recent `READ_CAP`
 * keys so the list never grows without bound.
 */
export const useTriageReadState = (uid: string | null): ReadState => {
  const key = READ_STORAGE_KEY(uid ?? "anon");
  const [keys, setKeys] = useState<string[]>(() => readJson<string[]>(key, []));

  useEffect(() => {
    setKeys(readJson<string[]>(key, []));
  }, [key]);

  // Functional updates so back-to-back marks (mark all + briefing) compose.
  const append = useCallback(
    (added: string[]) =>
      setKeys((prev) => {
        const have = new Set(prev);
        const fresh = added.filter((k) => !have.has(k));
        if (fresh.length === 0) return prev;
        const next = [...prev, ...fresh].slice(-READ_CAP);
        writeJson(key, next);
        return next;
      }),
    [key]
  );

  const set = useMemo(() => new Set(keys), [keys]);
  const isRead = useCallback(
    (item: TriageItem) => set.has(stateKey(item)),
    [set]
  );
  const markRead = useCallback(
    (items: TriageItem | TriageItem[]) => {
      const list = Array.isArray(items) ? items : [items];
      append(list.map(stateKey));
    },
    [append]
  );
  const briefingRead = set.has(BRIEFING_KEY);
  const markBriefingRead = useCallback(() => append([BRIEFING_KEY]), [append]);

  return { isRead, markRead, briefingRead, markBriefingRead };
};

type Dismissed = Record<string, { until: string | null }>;

type DismissedState = {
  isDismissed: (item: TriageItem, now?: Date) => boolean;
  /** Hide the item (until `until`, or until it changes when omitted). */
  dismiss: (item: TriageItem, until?: Date) => void;
  restore: (item: TriageItem) => void;
};

/**
 * Items the attorney dismissed or snoozed. Keyed by id + changedAt, so a
 * dismissed item comes back when the underlying record changes.
 */
export const useTriageDismissed = (uid: string | null): DismissedState => {
  const key = DISMISSED_STORAGE_KEY(uid ?? "anon");
  const [map, setMap] = useState<Dismissed>(() => readJson<Dismissed>(key, {}));

  useEffect(() => {
    setMap(readJson<Dismissed>(key, {}));
  }, [key]);

  const update = useCallback(
    (fn: (prev: Dismissed) => Dismissed) =>
      setMap((prev) => {
        const next = fn(prev);
        if (next !== prev) writeJson(key, next);
        return next;
      }),
    [key]
  );

  const isDismissed = useCallback(
    (item: TriageItem, now: Date = new Date()) => {
      const entry = map[stateKey(item)];
      if (!entry) return false;
      if (!entry.until) return true;
      return Date.parse(entry.until) > now.getTime();
    },
    [map]
  );
  const dismiss = useCallback(
    (item: TriageItem, until?: Date) =>
      update((prev) => ({
        ...prev,
        [stateKey(item)]: { until: until ? until.toISOString() : null }
      })),
    [update]
  );
  const restore = useCallback(
    (item: TriageItem) =>
      update((prev) => {
        if (!(stateKey(item) in prev)) return prev;
        const next = { ...prev };
        delete next[stateKey(item)];
        return next;
      }),
    [update]
  );

  return { isDismissed, dismiss, restore };
};

/** Tomorrow at 8am local: where "Snooze" puts an item. */
export const snoozeUntilTomorrow = (now: Date = new Date()): Date =>
  dayjs(now).add(1, "day").hour(8).minute(0).second(0).millisecond(0).toDate();
